// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import {fetchImage} from '../../../Utils/ImageSelector';
import {useCollection} from '@cloudscape-design/collection-hooks';
import {
    Alert,
    Box,
    Button,
    Header,
    Pagination,
    Select,
    SpaceBetween,
    Table,
    TextFilter,
} from '@cloudscape-design/components';
import * as R from 'ramda';
import React, {useEffect, useMemo} from 'react';
import {useResourcesProgressiveLoad} from '../../Hooks/useResources';
import {createTableAriaLabels} from '../../../Utils/AccessibilityUtils';
import {useResourceState} from '../../Contexts/ResourceContext';
import {useHistory} from 'react-router-dom';
import {useDebounce, useDeepCompareEffect} from 'react-use';
import {CREATE_DIAGRAM} from '../../../routes';
import PropTypes from 'prop-types';
import {useGetResourceGraph} from '../../Hooks/useGetResourceGraph';
import FilterControlsUI from './FilterControlsUI';
import {useFilterPersistence} from '../../Contexts/FilterPersistenceContext';
import {useExperimentsState} from '../../Contexts/ExperimentsContext';
import {
    filterDefaultResources,
    applyPrefixFilter,
    applyKeywordFilter,
} from '../../../Utils/ResourceFilteringUtils';

function ImageCell(item) {
    return (
        <img
            src={fetchImage(item.properties.resourceType)}
            style={{width: '25px', minHeight: '25px'}}
        />
    );
}

function ResourcesTable({accounts, resourceTypes}) {
    const [selectedItems, setSelectedItems] = React.useState([]);
    const [debouncedValue, setDebouncedValue] = React.useState('');
    
    // Check if experiments are enabled
    const {experimentsEnabled} = useExperimentsState();
    
    // Get all filter state from FilterPersistenceContext
    const {
        viewMode,
        excludePrefixes,
        keywordFilter,
        hideAutoGenerated,
        filterText,
        pageSize,
        updateFilter,
    } = useFilterPersistence();
    
    // If experiments are disabled, force viewMode to 'all' regardless of persisted state
    const effectiveViewMode = experimentsEnabled ? viewMode : 'all';
    
    // Parallel progressive loading to fetch all resources upfront
    // Fetches up to 10 batches of 1000 resources each in parallel (10,000 total)
    // This allows instant mode switching without re-fetching
    const {
        resources: allResources = [],
        isLoading: loadingResources,
        isComplete: loadingComplete,
        loadedBatches,
        totalBatches,
    } = useResourcesProgressiveLoad(
        debouncedValue,
        accounts,
        resourceTypes
    );

    // Apply filtering based on viewMode - ALL filtering happens client-side on cached data
    const filteredResources = useMemo(() => {
        let result = allResources;

        // Apply filtering based on effectiveViewMode (respects experiments toggle)
        if (effectiveViewMode === 'default') {
            // Hide Auto-Generated: Filter out auto-generated resources
            result = filterDefaultResources(result);
        } else if (effectiveViewMode === 'advanced') {
            // Custom Filters: Apply hideAutoGenerated first if enabled
            if (hideAutoGenerated) {
                result = filterDefaultResources(result);
            }
            // Then apply prefix and keyword filters
            result = applyPrefixFilter(result, excludePrefixes);
            result = applyKeywordFilter(result, keywordFilter);
        }
        // effectiveViewMode === 'all': Show all resources (no filtering)

        return result;
    }, [allResources, effectiveViewMode, excludePrefixes, keywordFilter, hideAutoGenerated]);

    // Calculate counts for display
    const totalResourceCount = allResources.length;
    const filteredResourceCount = filteredResources.length;
    const hiddenResourceCount = totalResourceCount - filteredResourceCount;

    const [, dispatch] = useResourceState();
    const history = useHistory();
    const {
        data: nodeData,
        refetch: loadSelected,
        isLoading,
        isError,
    } = useGetResourceGraph(R.map(e => e.properties.arn, selectedItems));

    useDebounce(
        () => {
            setDebouncedValue(filterText);
        },
        1000,
        [filterText]
    );

    useDeepCompareEffect(() => {
        // Reset selection when filters change
        setSelectedItems([]);
    }, [accounts, resourceTypes]);

    useEffect(() => {
        if (nodeData && !isError) {
            Promise.resolve(
                dispatch({
                    type: 'updateGraphResources',
                    graphResources: nodeData,
                })
            ).then(() => history.push(CREATE_DIAGRAM));
        }
    }, [nodeData, dispatch, history, isError]);

    const {items, filterProps, collectionProps, paginationProps} =
        useCollection(filteredResources, {
            filtering: {
                empty: (
                    <Box textAlign="center" color="inherit">
                        <b>No resources</b>
                        <Box
                            padding={{bottom: 's'}}
                            variant="p"
                            color="inherit"
                        >
                            Try selecting a resource type from the table above.
                        </Box>
                    </Box>
                ),
                noMatch: (
                    <Box textAlign="center" color="inherit">
                        <b>No match</b>
                        <Box
                            padding={{bottom: 's'}}
                            variant="p"
                            color="inherit"
                        >
                            No resources matched.
                        </Box>
                    </Box>
                ),
            },
            pagination: {pageSize},
            sorting: {sortingColumn: 'name'},
        });

    return (
        <Table
            ariaLabels={createTableAriaLabels(
                'resource',
                'resources',
                {
                    keys: ['title'],
                    fallback: 'Unknown resource name',
                },
                'Resources'
            )}
            {...collectionProps}
            trackBy={'id'}
            filter={
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%'
                }}>
                    <div style={{flex: '1', minWidth: '300px', maxWidth: '500px'}}>
                        <TextFilter
                            {...filterProps}
                            filteringPlaceholder="Find a resource"
                            filteringText={filterText}
                            onChange={({detail}) => updateFilter('filterText', detail.filteringText)}
                        />
                    </div>
                    <div style={{marginLeft: 'auto'}}>
                        <FilterControlsUI />
                    </div>
                </div>
            }
            loading={loadingResources}
            selectedItems={selectedItems}
            selectionType="multi"
            onSelectionChange={e => {
                dispatch({
                    type: 'select',
                    resources: e.detail.selectedItems,
                });
                setSelectedItems(e.detail.selectedItems);
            }}
            resizableColumns
            header={
                <Header
                    actions={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                gap: '12px',
                            }}
                        >
                            {selectedItems.length > 150 && (
                                <div
                                    style={{
                                        flexGrow: 1,
                                        maxWidth: 'calc(100% - 160px)',
                                    }}
                                >
                                    <Alert
                                        type="warning"
                                        dismissible={false}
                                        statusIconAriaLabel="too many selected resources warning"
                                    >
                                        Selecting this many resources will add
                                        hundreds, or potentially thousands, of
                                        resources to the diagram once the
                                        selected resources&apos; relationships
                                        have been traversed. This may impact
                                        performance.
                                    </Alert>
                                </div>
                            )}
                            <div style={{width: '160px', flexShrink: 0}}>
                                <Button
                                    disabled={R.isEmpty(selectedItems)}
                                    loading={isLoading}
                                    onClick={loadSelected}
                                    variant="primary"
                                >
                                    Add to diagram
                                </Button>
                            </div>
                        </div>
                    }
                >
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span>
                            {effectiveViewMode !== 'all' && hiddenResourceCount > 0
                                ? `Resources (${filteredResourceCount} visible, ${hiddenResourceCount} hidden)`
                                : `Resources (${filteredResourceCount})`}
                        </span>
                        {effectiveViewMode !== 'all' && hiddenResourceCount === 0 && totalResourceCount > 0 && (
                            <Box
                                color="text-status-inactive"
                                fontSize="body-s"
                                fontWeight="normal"
                            >
                                · No matches found
                            </Box>
                        )}
                    </div>
                </Header>
            }
            columnDefinitions={[
                {
                    id: 'icon',
                    minWidth: 75,
                    width: 75,
                    cell: ImageCell,
                },
                {
                    id: 'name',
                    header: 'Name',
                    cell: item => item.properties.title,
                    minWidth: 250,
                    width: 250,
                },
                {
                    id: 'type',
                    header: 'Type',
                    cell: item => item.properties.resourceType,
                    minWidth: 250,
                    width: 250,
                },
                {
                    id: 'account',
                    header: 'Account Id',
                    cell: item => item.properties.accountId,
                    minWidth: 150,
                    width: 150,
                },
                {
                    id: 'region',
                    header: 'Region',
                    cell: item => item.properties.awsRegion,
                    minWidth: 150,
                    width: 150,
                },
            ]}
            pagination={
                <Box textAlign="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Select
                            ariaLabel="search results page size"
                            selectedOption={{
                                label: `${pageSize} items`,
                                value: `${pageSize}`,
                            }}
                            onChange={({detail}) => {
                                const newPageSize = parseInt(
                                    detail.selectedOption.value
                                );
                                updateFilter('pageSize', newPageSize);
                            }}
                            options={[
                                {label: '10', value: '10'},
                                {label: '25', value: '25'},
                                {label: '50', value: '50'},
                                {label: '100', value: '100'},
                                {label: '250', value: '250'},
                                {label: '500', value: '500'},
                            ]}
                        />
                        <Pagination
                            {...paginationProps}
                            ariaLabels={{
                                nextPageLabel: 'Next page',
                                previousPageLabel: 'Previous page',
                                pageLabel: pageNumber =>
                                    `Page ${pageNumber} of all pages`,
                            }}
                        />
                    </SpaceBetween>
                </Box>
            }
            items={items}
        />
    );
}

ResourcesTable.propTypes = {
    accounts: PropTypes.array,
    resourceTypes: PropTypes.array,
};

export default ResourcesTable;
